import { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Search, Menu, X, User, LogOut, LayoutDashboard,
  MessageSquare, Plus, Shield, Bell, Mail, Phone
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { CATEGORY_LINKS, CategoryIcon } from "@/components/icons/GunIcons";
import AdBanner from "@/components/AdBanner";
import CookieConsent from "@/components/CookieConsent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = CATEGORY_LINKS;

const SITE_LINKS = [
  { label: "Gun specs", path: "/guns", testid: "nav-guns" },
  { label: "Compare prices", path: "/prices", testid: "nav-prices" },
  { label: "Dealers", path: "/dealers", testid: "nav-dealers" },
  { label: "Clubs", path: "/shooting-clubs", testid: "nav-clubs" },
  { label: "Events", path: "/events", testid: "nav-events" },
  { label: "Price checker", path: "/price-checker", testid: "nav-price-checker" },
  { label: "News", path: "/news", testid: "nav-news" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Dynamic self-referencing canonical tag + noindex for utility pages
  useEffect(() => {
    const canonical = document.getElementById("canonical-link");
    if (canonical) {
      canonical.setAttribute("href", `https://gunbuy.com.au${location.pathname}`);
    }
    // Add/remove noindex for pages that shouldn't be indexed
    const noIndexPaths = ["/login", "/register", "/sell", "/search", "/dashboard", "/inbox", "/admin", "/dealer-onboarding", "/verify-email", "/claim-account", "/claim-club"];
    const shouldNoIndex = noIndexPaths.some(p => location.pathname === p || location.pathname.startsWith(p + "/"));
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (shouldNoIndex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.name = "robots";
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = "noindex, nofollow";
    } else if (robotsMeta) {
      robotsMeta.content = "index, follow";
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => {
      axios.get(`${API}/notifications/unread-count`).then(r => setUnreadCount(r.data.count || 0)).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Skip Link for keyboard users */}
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-orange-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-sm focus:text-sm focus:font-semibold" data-testid="skip-link">
        Skip to main content
      </a>
      {/* Primary Header */}
      <header className="bg-slate-900 text-white" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4 lg:gap-3 xl:gap-6">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" aria-label="GunBuy home">
              <img src="/gunbuy-logo-white.svg" alt="GunBuy" className="h-7 sm:h-8 w-auto" />
            </Link>

            {/* Search Bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex lg:hidden xl:flex flex-1 min-w-0 max-w-lg" role="search">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" aria-hidden="true" />
                <Input
                  data-testid="header-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search make, model or calibre"
                  aria-label="Search listings"
                  className="pl-10 h-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 focus-visible:border-slate-500 focus-visible:ring-slate-500"
                />
              </div>
            </form>

            {/* Compact search at lg only — the full bar doesn't fit beside seven site links until xl */}
            <Button
              variant="ghost" size="icon"
              onClick={() => navigate("/search")}
              className="hidden lg:inline-flex xl:hidden text-slate-300 hover:text-white hover:bg-slate-800 shrink-0"
              aria-label="Search listings"
              data-testid="header-search-icon-btn"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Site links */}
            <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 ml-auto shrink-0" aria-label="Site">
              {SITE_LINKS.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  className={`px-1.5 xl:px-3 h-10 inline-flex items-center text-[13px] xl:text-sm font-medium whitespace-nowrap rounded-sm transition-colors ${location.pathname.startsWith(l.path) ? "text-white bg-slate-800" : "text-slate-300 hover:text-white hover:bg-slate-800"}`}
                  data-testid={l.testid}
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2 ml-auto lg:ml-0">
              {user ? (
                <>
                  {(user.account_type === "private_seller" || user.account_type === "dealer") && user.verification_status === "verified" && (
                    <Button
                      data-testid="create-listing-btn"
                      onClick={() => navigate("/sell")}
                      variant="accent"
                      className="hidden sm:inline-flex gap-1.5"
                    >
                      <Plus className="h-4 w-4" /> Sell an item
                    </Button>
                  )}
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => navigate("/dashboard/inbox")}
                    className="text-slate-300 hover:text-white hover:bg-slate-800"
                    data-testid="inbox-btn"
                    aria-label="Messages"
                  >
                    <MessageSquare className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => navigate("/dashboard?tab=notifications")}
                    className="relative text-slate-300 hover:text-white hover:bg-slate-800"
                    data-testid="notification-bell"
                    aria-label="Notifications"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-600 text-[10px] font-bold text-white px-1 num" data-testid="unread-badge">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="user-menu-btn" aria-label="Account menu">
                        <User className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60 rounded-sm border-slate-200">
                      <div className="px-3 py-2">
                        <p className="text-sm font-semibold text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        {user.verification_status === "pending" && (
                          <Badge variant="outline" className="mt-1.5 text-amber-700 border-amber-300 bg-amber-50 text-xs">Pending verification</Badge>
                        )}
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="dashboard-menu-item">
                        <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/dashboard/inbox")} data-testid="inbox-menu-item">
                        <MessageSquare className="h-4 w-4 mr-2" /> Messages
                      </DropdownMenuItem>
                      {user.account_type === "admin" && (
                        <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-menu-item">
                          <Shield className="h-4 w-4 mr-2" /> Admin Panel
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                        <LogOut className="h-4 w-4 mr-2" /> Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate("/login")}
                    className="hidden sm:inline-flex bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:border-slate-500 gap-2"
                    data-testid="login-btn"
                  >
                    <User className="h-4 w-4" /> Sign in
                  </Button>
                  <Button
                    variant="accent"
                    onClick={() => navigate("/register")}
                    data-testid="register-btn"
                  >
                    Sell an item
                  </Button>
                </div>
              )}
              <Button
                variant="ghost" size="icon"
                className="md:hidden text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Secondary Nav */}
      <nav className="bg-white border-b border-slate-200 hidden md:block" data-testid="category-nav" aria-label="Categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {CATEGORIES.map((cat) => {
              const active = location.pathname === cat.path || location.pathname.startsWith(cat.path + "/");
              return (
                <Link
                  key={cat.path}
                  to={cat.path}
                  className={`group h-12 px-1 inline-flex items-center gap-1.5 text-[12px] font-medium whitespace-nowrap border-b-2 transition-colors ${active ? "text-slate-900 border-orange-600" : "text-slate-600 border-transparent hover:text-slate-900 hover:border-slate-300"}`}
                  data-testid={`nav-${cat.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <CategoryIcon category={cat.key} className={`hidden 2xl:block h-4 w-4 shrink-0 ${active ? "text-orange-600" : "text-slate-400 group-hover:text-slate-700"}`} />
                  {cat.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200" data-testid="mobile-menu">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="mb-3" role="search">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" aria-hidden="true" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search make, model or calibre"
                  aria-label="Search listings"
                  className="pl-10 h-11"
                  data-testid="mobile-search-input"
                />
              </div>
            </form>
            <div className="grid grid-cols-2 gap-1">
              {CATEGORIES.map((cat) => (
                <Link
                  key={cat.path}
                  to={cat.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 min-h-[44px] text-sm font-medium text-slate-800 hover:bg-slate-50 rounded-sm"
                >
                  <CategoryIcon category={cat.key} className="h-4 w-4 text-slate-500 shrink-0" />
                  {cat.label}
                </Link>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 gap-1">
              <Link to="/dealers" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 min-h-[44px] text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-sm">
                Find a Dealer
              </Link>
              <Link to="/shooting-clubs" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 min-h-[44px] text-sm font-medium text-slate-800 hover:bg-slate-50 rounded-sm">
                Shooting Clubs
              </Link>
              <Link to="/events" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 min-h-[44px] text-sm font-medium text-slate-800 hover:bg-slate-50 rounded-sm" data-testid="mobile-nav-events">
                Shooting Events
              </Link>
              <Link to="/price-checker" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 min-h-[44px] text-sm font-medium text-slate-800 hover:bg-slate-50 rounded-sm">
                Price Checker
              </Link>
              <Link to="/guns" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 min-h-[44px] text-sm font-medium text-slate-800 hover:bg-slate-50 rounded-sm">
                Gun Specs
              </Link>
              <Link to="/prices" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 min-h-[44px] text-sm font-medium text-slate-800 hover:bg-slate-50 rounded-sm">
                Compare Prices
              </Link>
              <Link to="/news" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 min-h-[44px] text-sm font-medium text-slate-800 hover:bg-slate-50 rounded-sm">
                News & Updates
              </Link>
              {!user && (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-3 min-h-[44px] text-sm font-semibold text-slate-900 hover:bg-slate-50 rounded-sm sm:hidden">
                  Sign in
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main id="main" className="flex-1">
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Footer Ad */}
      <AdBanner placement="footer" className="my-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" />

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-16" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8">
            {/* Brand */}
            <div className="col-span-2 sm:col-span-3 lg:col-span-2">
              <div className="mb-4">
                <img src="/gunbuy-logo.svg" alt="GunBuy" className="h-7 w-auto" />
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4 max-w-sm">
                Australia's premier classifieds for used firearms, accessories and services — connecting licensed individuals and dealers.
              </p>
              <div className="space-y-2 mb-5">
                <p className="text-sm text-slate-900 font-semibold" data-testid="footer-business-name">gunbuy.com.au</p>
                <p className="text-sm text-slate-600" data-testid="footer-address">PO Box 559 Redbank Plains QLD 4301</p>
                <a href="tel:+12569180230" className="text-sm text-slate-600 hover:text-orange-600 transition-colors flex items-center gap-1.5" data-testid="footer-phone">
                  <Phone className="h-3.5 w-3.5" /> +1 256 918 0230
                </a>
                <a href="mailto:hello@gunbuy.com.au" className="text-sm text-slate-600 hover:text-orange-600 transition-colors flex items-center gap-1.5" data-testid="footer-email">
                  <Mail className="h-3.5 w-3.5" /> hello@gunbuy.com.au
                </a>
              </div>
              <div className="flex items-center gap-3" data-testid="footer-social-links">
                <a href="https://www.facebook.com/gunbuyau" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors" aria-label="Facebook" data-testid="footer-social-facebook">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.instagram.com/gunbuy.com.au/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors" aria-label="Instagram" data-testid="footer-social-instagram">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://x.com/gunbuyau" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors" aria-label="X" data-testid="footer-social-x">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://www.reddit.com/r/firearmsaus/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors" aria-label="Reddit" data-testid="footer-social-reddit">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z"/></svg>
                </a>
                <a href="https://share.google/Y0ScxiUP3LHrC8iNX" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors" aria-label="Google My Business" data-testid="footer-social-google">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                </a>
                <a href="https://www.linkedin.com/company/gunbuy-com-au/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors" aria-label="LinkedIn" data-testid="footer-social-linkedin">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
              {/* Newsletter Signup */}
              <div className="mt-5" data-testid="footer-newsletter">
                <p className="text-sm font-semibold text-slate-900 mb-2">Get new listings in your inbox</p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const email = e.target.elements.email.value.trim();
                  if (!email) return;
                  try {
                    await axios.post(`${API}/newsletter/subscribe`, { email });
                    toast.success("Subscribed! Check your inbox.");
                    e.target.reset();
                  } catch (err) { toast.error(err.response?.data?.detail || "Could not subscribe"); }
                }} className="flex gap-2">
                  <Input name="email" type="email" required placeholder="you@example.com" aria-label="Email address" className="h-10 text-sm max-w-xs" data-testid="newsletter-email" />
                  <Button type="submit" className="shrink-0 h-10 px-4" data-testid="newsletter-submit">Subscribe</Button>
                </form>
              </div>
            </div>
            {/* Categories */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-900 mb-4">Categories</h4>
              <ul className="space-y-1.5">
                {CATEGORIES.map((cat) => (
                  <li key={cat.path}>
                    <Link to={cat.path} className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1 leading-relaxed">{cat.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            {/* Browse by State */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-900 mb-4">Browse by State</h4>
              <ul className="space-y-2">
                <li><Link to="/rifles/new-south-wales" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">New South Wales</Link></li>
                <li><Link to="/rifles/victoria" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">Victoria</Link></li>
                <li><Link to="/rifles/queensland" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">Queensland</Link></li>
                <li><Link to="/rifles/western-australia" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">Western Australia</Link></li>
                <li><Link to="/rifles/south-australia" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">South Australia</Link></li>
                <li><Link to="/rifles/tasmania" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">Tasmania</Link></li>
                <li><Link to="/rifles/northern-territory" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">Northern Territory</Link></li>
                <li><Link to="/rifles/australian-capital-territory" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">ACT</Link></li>
              </ul>
            </div>
            {/* Explore */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-900 mb-4">Explore</h4>
              <ul className="space-y-2">
                <li><Link to="/dealers" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">Dealer Directory</Link></li>
                <li><Link to="/shooting-clubs" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-clubs-link">Shooting Clubs</Link></li>
                <li><Link to="/events" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-events-link">Shooting Events</Link></li>
                <li><Link to="/price-checker" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-price-checker-link">Gun Price Checker</Link></li>
                <li><Link to="/guns" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-guns-link">Gun Specs &amp; Model Guides</Link></li>
                <li><Link to="/prices" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-prices-link">Gun Price Comparison</Link></li>
                <li><Link to="/news" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">News & Updates</Link></li>
                <li><Link to="/register" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1">Register to List</Link></li>
                <li><Link to="/advertise" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-advertise-link">Advertise With Us</Link></li>
              </ul>
            </div>
            {/* Legal */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-900 mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-terms-link">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/contact" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-contact-link">Contact Us</Link></li>
                <li><Link to="/complaints-policy" className="text-sm text-slate-600 hover:text-orange-600 transition-colors inline-block py-1" data-testid="footer-complaints-link">Complaints Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 mt-8 pt-8 text-center">
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} gunbuy.com.au. All rights reserved. All users must hold a valid Australian firearms licence.</p>
            <p className="text-xs text-slate-500 mt-1">All transactions on GunBuy must be facilitated through a licensed dealer in accordance with Australian firearms law.</p>
          </div>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}
